import React, { useState } from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { API_BASE } from '../consts';

const OnboardingModal = ({ onComplete }) => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        marketplaces_sell_on: [],
        how_found_us: ''
    });

    const marketplaces = ['Amazon', 'Walmart', 'eBay', 'AIP'];

    const toggleSelection = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(value)
                ? prev[field].filter(item => item !== value)
                : [...prev[field], value]
        }));
    };

    const handleNext = () => {
        if (step < 4) {
            setStep(step + 1);
        }
    };

    const handleComplete = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/user/onboarding`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-User-Email': localStorage.getItem('credentials') ? JSON.parse(localStorage.getItem('credentials')).email : '',
                    'X-User-Password': localStorage.getItem('credentials') ? JSON.parse(localStorage.getItem('credentials')).password : ''
                },
                body: JSON.stringify({
                    name: formData.name,
                    marketplaces_sell_on: formData.marketplaces_sell_on,
                    how_found_us: formData.how_found_us,
                    onboarding_completed: true
                })
            });

            if (response.ok) {
                onComplete();
            }
        } catch (error) {
            console.error('Failed to save onboarding data:', error);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Progress Bar */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
                    <div className="flex justify-between mb-2">
                        {[1, 2, 3, 4].map((s) => (
                            <div
                                key={s}
                                className={`h-2 flex-1 mx-1 rounded-full ${s <= step ? 'bg-gradient-to-r from-teal-500 to-blue-600' : 'bg-gray-200'}`}
                            />
                        ))}
                    </div>
                    <p className="text-sm text-gray-600 text-center">Step {step} of 4</p>
                </div>

                <div className="p-8">
                    {/* Step 1: Welcome */}
                    {step === 1 && (
                        <div className="text-center">
                            <div className="mb-6">
                                <img src="/mspfull.png" alt="MSP" className="h-20 w-auto mx-auto" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Welcome to MultiSellerPro! 🎉
                            </h2>
                            <p className="text-gray-600 mb-8">
                                Let's get you set up in just a few quick steps. This will help us personalize your experience and show you the tools most relevant to your business.
                            </p>
                            <button
                                onClick={handleNext}
                                className="bg-gradient-to-r from-teal-500 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition flex items-center gap-2 mx-auto"
                            >
                                Get Started <ArrowRight size={20} />
                            </button>
                        </div>
                    )}

                    {/* Step 2: Name Input */}
                    {step === 2 && (
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                What's your name?
                            </h2>
                            <p className="text-gray-600 mb-6">Let's personalize your experience</p>

                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Enter your name"
                                className="w-full p-4 border-2 border-gray-300 rounded-lg mb-8 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 text-lg"
                                autoFocus
                            />

                            <button
                                onClick={handleNext}
                                disabled={!formData.name.trim()}
                                className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continue
                            </button>
                        </div>
                    )}

                    {/* Step 3: Marketplaces */}
                    {step === 3 && (
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Which platform is your area of focus?
                            </h2>
                            <p className="text-gray-600 mb-6">Select all that apply</p>
                            <div className="grid grid-cols-2 gap-4 mb-8">
                                {marketplaces.map((marketplace) => (
                                    <button
                                        key={marketplace}
                                        onClick={() => toggleSelection('marketplaces_sell_on', marketplace)}
                                        className={`p-3 border-2 rounded-lg font-semibold transition flex flex-col items-center justify-center gap-1 ${formData.marketplaces_sell_on.includes(marketplace)
                                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                                            : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                    >
                                        <img
                                            src={`/icons/${marketplace.toLowerCase().replace(' shop', '').replace(/\s+/g, '-')}.png`}
                                            alt=""
                                            className="h-8 w-8 object-contain flex-shrink-0"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        <span>{marketplace}</span>
                                    </button>
                                ))}
                            </div>
                            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-4" />
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setStep(2)}
                                    className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleNext}
                                    disabled={!formData.marketplaces_sell_on.length}
                                    className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: How did you find us + Walkthrough */}
                    {step === 4 && (
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                How did you find us?
                            </h2>
                            <p className="text-gray-600 mb-6">Optional - helps us improve</p>

                            <textarea
                                value={formData.how_found_us}
                                onChange={(e) => setFormData({ ...formData, how_found_us: e.target.value })}
                                placeholder="YouTube, friend referral, Facebook ad, etc."
                                className="w-full p-4 border-2 border-gray-300 rounded-lg mb-8 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                                rows={3}
                            />

                            {/* Quick Walkthrough */}
                            <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-6 rounded-xl mb-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-4">
                                    You made the right call - hit play and join us!
                                </h3>
                                <div className="mt-6">
                                    <div className="bg-gray-900 rounded-lg overflow-hidden">
                                        <iframe
                                            width="100%"
                                            height="300"
                                            src="https://www.youtube.com/embed/0d-eZEVcKRo"
                                            title="MSP Introduction"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Community Links */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                                <h3 className="text-base font-semibold text-gray-900 mb-3">
                                    🌟 Join Our Communities
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    <a
                                        href="/communities"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-center text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-1"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                        </svg>
                                        Facebook
                                    </a>
                                    <a
                                        href="/communities"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-indigo-600 text-white py-2 px-3 rounded-lg text-center text-sm font-medium hover:bg-indigo-700 flex items-center justify-center gap-1"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
                                        </svg>
                                        Discord
                                    </a>
                                    <a
                                        href="/webinars"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg text-center text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-1"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Webinars
                                    </a>
                                    <a
                                        href="https://www.youtube.com/@MultiSellerPro"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-red-600 text-white py-2 px-3 rounded-lg text-center text-sm font-medium hover:bg-red-700 flex items-center justify-center gap-1"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                        </svg>
                                        YouTube
                                    </a>
                                    <a
                                        href="https://x.com/MultiSellerPro"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-black text-white py-2 px-3 rounded-lg text-center text-sm font-medium hover:bg-gray-800 flex items-center justify-center gap-1"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                        Twitter
                                    </a>
                                </div>
                            </div>

                            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-4" />
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setStep(3)}
                                    className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleComplete}
                                    className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90"
                                >
                                    Complete Setup
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingModal;